import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  checkConsentForSend,
  createCustomerDebtReportLink,
  createPaymentLink,
  getCustomerDebtReport,
  getCustomerOpenInvoices,
  getRivhitCustomers,
  getSettings,
  logCustomerInteraction,
  renderMessageTemplate,
  searchRivhitCustomers,
  sendPaymentNotification,
  type PaymentNotificationChannel,
  type PaymentRequestInvoiceSnapshot,
} from '../api/client';
import { sortInvoicesOldestFirst, summarizeOpenInvoicesByScope } from '../lib/openBalanceDebtScope';
import { normalizePhoneForWhatsAppLink } from '../lib/phone';
import EditMessageTemplateModal from './EditMessageTemplateModal';

/* ── Types ───────────────────────────────────────────────────────────── */

interface RivhitCustomer {
  customer_id: number;
  last_name?: string;
  first_name?: string;
  phone?: string;
  email?: string;
}

interface RivhitCustomersResponse {
  data?: RivhitCustomer[];
}

interface OpenInvoice {
  document_type: number;
  document_number: number;
  document_date: string;
  document_type_name: string;
  amount: number;
  paid_amount: number;
  open_balance: number;
}

interface InvoiceAllocation {
  document_type: number;
  document_number: number;
  amount_close: number;
  invoice_total?: number;
  open_balance?: number;
  document_date?: string;
}

export interface PaymentLinkWizardProps {
  /** Pre-selected customer — skips the customer selection step */
  customerId?: number;
  customerName?: string;
  /** Render inline (no modal backdrop) for full-page use */
  inline?: boolean;
  onClose?: () => void;
  onSuccess?: (url: string, invoiceNumbers?: number[]) => void;
}

/* ── Helpers ─────────────────────────────────────────────────────────── */

function customerDisplayName(c: RivhitCustomer): string {
  return [c.first_name, c.last_name].filter(Boolean).join(' ') || `#${c.customer_id}`;
}

function extractCustomers(payload: RivhitCustomer[] | RivhitCustomersResponse | null | undefined): RivhitCustomer[] {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function formatCurrency(n: number): string {
  return n.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' });
}

type WizardStep = 'customer' | 'invoices';

/* ── Component ───────────────────────────────────────────────────────── */

export default function PaymentLinkWizardModal({
  customerId: preselectedCustomerId,
  customerName: preselectedCustomerName,
  inline = false,
  onClose,
  onSuccess,
}: PaymentLinkWizardProps) {
  const { t } = useTranslation();

  const customerKnown = preselectedCustomerId != null;
  const [step, setStep] = useState<WizardStep>(customerKnown ? 'invoices' : 'customer');

  // Customer selection state
  const [customers, setCustomers] = useState<RivhitCustomer[]>([]);
  const [searchResults, setSearchResults] = useState<RivhitCustomer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<RivhitCustomer | null>(
    customerKnown
      ? { customer_id: preselectedCustomerId!, first_name: preselectedCustomerName }
      : null,
  );

  // Invoice selection state
  const [invoices, setInvoices] = useState<OpenInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [unknownBalanceCount, setUnknownBalanceCount] = useState(0);
  const [allocations, setAllocations] = useState<InvoiceAllocation[]>([]);
  const [paymentTermsDays, setPaymentTermsDays] = useState(60);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationChannel, setNotificationChannel] = useState<PaymentNotificationChannel>('sms');
  const [paymentUrl, setPaymentUrl] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [templateRevision, setTemplateRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [smsSentTo, setSmsSentTo] = useState<string | null>(null);
  const [settings, setSettings] = useState<any>(null);
  const [whatsAppAudit, setWhatsAppAudit] = useState<{
    requestId: string;
    opened: boolean;
    phone: string;
    message: string;
    invoiceIds: string[];
    paymentLink: string;
  } | null>(null);
  const [loggingWhatsApp, setLoggingWhatsApp] = useState(false);

  // Current all-open-invoices debt-report link fetched fresh for this payment
  // link (not just the selected/allocated invoices). Used to enrich the
  // WhatsApp message before Copy Message/wa.me are enabled.
  const [debtReportUrl, setDebtReportUrl] = useState('');
  const [debtReportLoading, setDebtReportLoading] = useState(false);
  const [debtReportError, setDebtReportError] = useState<string | null>(null);
  const [debtReportRetryToken, setDebtReportRetryToken] = useState(0);

  // Reset payment link whenever invoice selection changes after a link was generated
  useEffect(() => {
    if (!paymentUrl && !smsSentTo) return;
    setPaymentUrl('');
    setSmsSentTo(null);
    setError(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocations]);

  // Request a fresh, current (all open invoices) debt-report link as soon as
  // the provider payment URL is ready. Re-fetched whenever the customer or
  // payment URL selection resets (paymentUrl cleared then a new link is
  // generated), or when the operator explicitly retries after a failure.
  useEffect(() => {
    if (!paymentUrl || !selectedCustomer) {
      setDebtReportUrl('');
      setDebtReportLoading(false);
      setDebtReportError(null);
      return;
    }
    let cancelled = false;
    setDebtReportLoading(true);
    setDebtReportError(null);
    setDebtReportUrl('');
    createCustomerDebtReportLink(selectedCustomer.customer_id, { locale: 'he' })
      .then((res) => {
        if (!cancelled) setDebtReportUrl(res.data.short_url);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setDebtReportError(
            err?.response?.data?.detail || t('paymentLinkWizard.debtReportError', 'Failed to prepare the debt report link'),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setDebtReportLoading(false);
      });
    return () => { cancelled = true; };
  }, [paymentUrl, selectedCustomer, debtReportRetryToken, t]);

  /* ── Load initial customers + server-side search ──────────────────────── */

  useEffect(() => {
    getSettings().then((res) => setSettings(res.data?.settings || res.data));
  }, []);

  useEffect(() => {
    if (customerKnown) return;

    setCustomersLoading(true);
    getRivhitCustomers()
      .then((res) => setCustomers(extractCustomers(res.data)))
      .catch(() => setCustomers([]))
      .finally(() => setCustomersLoading(false));
  }, [customerKnown]);

  useEffect(() => {
    if (customerKnown) return;
    const query = customerSearch.trim();
    if (!query) {
      setSearchResults([]);
      return;
    }

    let cancelled = false;
    const timeout = window.setTimeout(() => {
      setSearchLoading(true);
      searchRivhitCustomers(query, 50)
        .then((res) => {
          if (!cancelled) setSearchResults(res.data || []);
        })
        .catch(() => {
          if (!cancelled) setSearchResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearchLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [customerKnown, customerSearch]);

  /* ── Load invoices when customer is selected ────────────────────────── */

  useEffect(() => {
    if (!selectedCustomer) {
      setInvoices([]);
      setUnknownBalanceCount(0);
      setAllocations([]);
      setPaymentTermsDays(60);
      return;
    }

    let cancelled = false;
    setInvoicesLoading(true);
    setError(null);
    setAllocations([]);
    getCustomerOpenInvoices(selectedCustomer.customer_id)
      .then((res) => {
        if (!cancelled) {
          const rows = res.data || [];
          const knownInvoices: OpenInvoice[] = rows
            .filter((invoice) => invoice.balance_status !== 'unknown' && invoice.open_balance != null)
            .map((invoice) => ({
              ...invoice,
              paid_amount: invoice.paid_amount || 0,
              open_balance: Number(invoice.open_balance),
            }));
          setUnknownBalanceCount(rows.length - knownInvoices.length);
          setInvoices(sortInvoicesOldestFirst(knownInvoices, (invoice) => invoice.document_date));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setInvoices([]);
          setError(err?.response?.data?.detail || t('paymentLinkWizard.openInvoicesError'));
        }
      })
      .finally(() => {
        if (!cancelled) setInvoicesLoading(false);
      });

    getCustomerDebtReport(selectedCustomer.customer_id)
      .then((res) => {
        if (!cancelled) setPaymentTermsDays(res.data?.payment_terms_days ?? 60);
      })
      .catch(() => {
        if (!cancelled) setPaymentTermsDays(60);
      });

    return () => { cancelled = true; };
  }, [selectedCustomer, t]);

  useEffect(() => {
    if (!selectedCustomer) return;
    checkConsentForSend(String(selectedCustomer.customer_id))
      .catch(() => { /* ignore consent errors */ });
  }, [selectedCustomer]);

  /* ── Customer options (merge local page + server search) ──────────────── */

  const isSearching = customersLoading || searchLoading;

  const customerOptions = useMemo(() => {
    const query = customerSearch.trim();
    if (!query) return customers;
    const q = query.toLowerCase();
    const localMatches = customers.filter((c) => {
      const name = customerDisplayName(c).toLowerCase();
      const id = String(c.customer_id);
      const phone = c.phone?.toLowerCase() || '';
      const email = c.email?.toLowerCase() || '';
      return name.includes(q) || id.includes(q) || phone.includes(q) || email.includes(q);
    });
    const merged = new Map<number, RivhitCustomer>();
    for (const c of searchResults) merged.set(c.customer_id, c);
    for (const c of localMatches) merged.set(c.customer_id, c);
    return Array.from(merged.values());
  }, [customers, customerSearch, searchResults]);

  /* ── Invoice allocation helpers ─────────────────────────────────────── */

  const selectedSet = useMemo(() => {
    const m = new Map<string, InvoiceAllocation>();
    for (const a of allocations) m.set(`${a.document_type}#${a.document_number}`, a);
    return m;
  }, [allocations]);

  const selectedTotal = useMemo(
    () => allocations.reduce((sum, a) => sum + (a.amount_close || 0), 0),
    [allocations],
  );

  const allocationsValid = useMemo(
    () =>
      allocations.length > 0 &&
      allocations.every((a) => {
        const ob = a.open_balance ?? 0;
        return a.amount_close > 0 && a.amount_close <= ob + 0.01;
      }),
    [allocations],
  );

  const canCreate = !!selectedCustomer && selectedTotal > 0 && allocationsValid && !submitting && !paymentUrl;
  const canSendNotification = !!selectedCustomer && selectedTotal > 0 && allocationsValid && !sendingNotification && !!paymentUrl;

  const selectedInvoiceSnapshots = useMemo<PaymentRequestInvoiceSnapshot[]>(() => {
    return allocations.map((allocation) => {
      const invoice = invoices.find(
        (inv) => inv.document_type === allocation.document_type && inv.document_number === allocation.document_number,
      );
      const id = `${allocation.document_type}-${allocation.document_number}`;
      return {
        invoice_id: id,
        document_number: String(allocation.document_number),
        issue_date: allocation.document_date || invoice?.document_date,
        open_amount: Number((allocation.amount_close || 0).toFixed(2)),
        currency: 'ILS',
        description: invoice?.document_type_name,
        status: 'open',
      };
    });
  }, [allocations, invoices]);

  const selectedInvoiceIds = useMemo(
    () => selectedInvoiceSnapshots.map((invoice) => invoice.invoice_id),
    [selectedInvoiceSnapshots],
  );

  const toggleInvoice = useCallback(
    (invoice: OpenInvoice) => {
      const key = `${invoice.document_type}#${invoice.document_number}`;
      if (selectedSet.has(key)) {
        setAllocations((cur) =>
          cur.filter(
            (a) => a.document_type !== invoice.document_type || a.document_number !== invoice.document_number,
          ),
        );
      } else {
        setAllocations((cur) => [
          ...cur,
          {
            document_type: invoice.document_type,
            document_number: invoice.document_number,
            amount_close: Number(invoice.open_balance.toFixed(2)),
            invoice_total: invoice.amount,
            open_balance: invoice.open_balance,
            document_date: invoice.document_date,
          },
        ]);
      }
    },
    [selectedSet],
  );

  const toggleSelectAll = useCallback(() => {
    if (allocations.length === invoices.length) {
      setAllocations([]);
    } else {
      setAllocations(
        invoices.map((inv) => ({
          document_type: inv.document_type,
          document_number: inv.document_number,
          amount_close: Number(inv.open_balance.toFixed(2)),
          invoice_total: inv.amount,
          open_balance: inv.open_balance,
          document_date: inv.document_date,
        })),
      );
    }
  }, [allocations.length, invoices]);

  const updateAllocationAmount = useCallback((invoice: OpenInvoice, value: string) => {
    const nextAmount = Number(value);
    setAllocations((cur) =>
      cur.map((a) =>
        a.document_type === invoice.document_type && a.document_number === invoice.document_number
          ? { ...a, amount_close: Number.isFinite(nextAmount) ? nextAmount : 0 }
          : a,
      ),
    );
  }, []);

  /* ── Create payment link ────────────────────────────────────────────── */

  const handleCreate = useCallback(async () => {
    if (!selectedCustomer || !canCreate) return;
    setSubmitting(true);
    setError(null);
    setPaymentUrl('');
    try {
      const nums = allocations.map((a) => a.document_number).join(', ');
      const res = await createPaymentLink({
        customer_id: selectedCustomer.customer_id,
        invoices_to_close: allocations,
        description: `Payment for invoices: ${nums}`,
        reference: selectedCustomer.customer_id,
      });
      const customerFacingUrl = res.data.short_url || res.data.url;
      setPaymentUrl(customerFacingUrl);
      onSuccess?.(customerFacingUrl, allocations.map((a) => a.document_number));
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Only surface a server-provided message for client-side (4xx) errors
      // that carry an actionable, plain-language detail. For server/gateway
      // failures (5xx, 502/503/504) or transport errors, show a friendly
      // message instead of echoing raw provider text like "502 Bad Gateway".
      const useDetail = typeof detail === 'string' && detail.trim().length > 0
        && status && status >= 400 && status < 500;
      setError(useDetail ? detail : t('paymentLinkWizard.createError'));
    } finally {
      setSubmitting(false);
    }
  }, [allocations, canCreate, selectedCustomer, t, onSuccess]);

  const handleSendPaymentNotification = useCallback(async () => {
    if (!selectedCustomer || !canSendNotification) return;
    setSendingNotification(true);
    setError(null);
    setSmsSentTo(null);
    try {
      const res = await sendPaymentNotification({
        customer_id: String(selectedCustomer.customer_id),
        invoice_ids: selectedInvoiceIds,
        invoice_snapshots: selectedInvoiceSnapshots,
        channel: notificationChannel,
        include_whatsapp_handoff: true,
        reuse_active_request: true,
      });
      const pr = res.data.payment_request;
      const displayUrl = pr.short_payment_link || pr.public_payment_link;
      setPaymentUrl(displayUrl);
      const phone = selectedCustomer.phone || '';
      setSmsSentTo(phone);
      onSuccess?.(displayUrl, allocations.map((a) => a.document_number));
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('paymentLinkWizard.sendSmsError', 'Failed to send payment notification'));
    } finally {
      setSendingNotification(false);
    }
  }, [canSendNotification, notificationChannel, onSuccess, selectedCustomer, selectedInvoiceIds, selectedInvoiceSnapshots, t]);

  const whatsappTemplateValues = useMemo(() => {
    if (!selectedCustomer || !paymentUrl || !settings) return null;
    // Wait for the fresh, current (all-open-invoices) debt-report link
    // before building the message — Copy Message/wa.me must never fire
    // with a stale or missing report line.
    if (debtReportLoading || debtReportError || !debtReportUrl) return null;
    const bizName = settings.customer_bot_business_name || settings.business_name || 'Dada Coffee';
    const nums = allocations.map((a) => a.document_number).join(', ');
    const customerName = [selectedCustomer.first_name, selectedCustomer.last_name].filter(Boolean).join(' ').trim();

    const totalSummary = summarizeOpenInvoicesByScope(invoices, paymentTermsDays, 'all');
    const agedSummary = summarizeOpenInvoicesByScope(invoices, paymentTermsDays, 'aged');
    const agedOpen = agedSummary.total;
    const agedClause = agedOpen > 0
      ? `, מתוכה ₪${agedOpen.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} חוב ישן מעל ${paymentTermsDays} יום`
      : '';
    const balanceSentence = `יש לך חוב כולל בסך ₪${totalSummary.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${agedClause}.`;

    return {
      business_name: bizName,
      customer_name: customerName,
      invoice_count: String(allocations.length),
      invoice_label: allocations.length > 1 ? 'חשבוניות' : 'חשבונית',
      invoice_numbers: nums,
      payment_link: paymentUrl,
      balance_sentence: balanceSentence,
      // Raw report URL only — the server centrally derives/appends the
      // formatted, localized report line so no formatting is duplicated here.
      debt_report_url: debtReportUrl,
    };
  }, [paymentUrl, settings, allocations, selectedCustomer, invoices, paymentTermsDays, debtReportUrl, debtReportLoading, debtReportError]);

  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  useEffect(() => {
    if (!whatsappTemplateValues) {
      setWhatsappMessage('');
      setTemplateLoading(false);
      setTemplateError(null);
      return;
    }
    let active = true;
    setTemplateLoading(true);
    setTemplateError(null);
    renderMessageTemplate('payment_link_wizard_whatsapp', whatsappTemplateValues)
      .then((response) => {
        if (active) setWhatsappMessage(response.data.body);
      })
      .catch((err: any) => {
        if (active) {
          setWhatsappMessage('');
          setTemplateError(err?.response?.data?.detail || t('paymentLinkWizard.templateRenderError', 'Failed to generate the message'));
        }
      })
      .finally(() => {
        if (active) setTemplateLoading(false);
      });
    return () => {
      active = false;
    };
  }, [whatsappTemplateValues, templateRevision, t]);

  // Message/report readiness gate for Copy Message and Send via WhatsApp —
  // neither may fire until the fresh debt report was fetched and the
  // template rendered successfully.
  const messageReady = !debtReportLoading && !debtReportError && !templateLoading && !templateError && !!whatsappMessage;
  const handleRetryDebtReport = useCallback(() => {
    setDebtReportRetryToken((current) => current + 1);
  }, []);

  const handleSendWhatsApp = useCallback(async () => {
    if (!selectedCustomer?.phone || !whatsappMessage) return;
    const audit = whatsAppAudit || {
      requestId: crypto.randomUUID(),
      opened: false,
      phone: selectedCustomer.phone,
      message: whatsappMessage,
      invoiceIds: selectedInvoiceIds,
      paymentLink: paymentUrl,
    };
    setError(null);
    setLoggingWhatsApp(true);
    if (!audit.opened) {
      const phone = normalizePhoneForWhatsAppLink(audit.phone);
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(audit.message)}`, '_blank', 'noopener,noreferrer');
      audit.opened = true;
      setWhatsAppAudit(audit);
    }
    try {
      await logCustomerInteraction(selectedCustomer.customer_id, {
        channel: 'whatsapp',
        message_type: 'payment_link_sent',
        selected_action: 'payment_link_handoff_opened',
        destination: audit.phone,
        message_text: audit.message,
        related_invoice_ids: audit.invoiceIds,
        related_payment_link_id: audit.paymentLink,
        request_id: audit.requestId,
      });
      setWhatsAppAudit(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('customerBot.interactionSaveError', 'WhatsApp opened, but communication history could not be saved. Press again to retry saving without opening another draft.'));
    } finally {
      setLoggingWhatsApp(false);
    }
  }, [paymentUrl, selectedCustomer, selectedInvoiceIds, t, whatsAppAudit, whatsappMessage]);

  const handleCopyMessage = useCallback(async () => {
    if (!whatsappMessage) return;
    await navigator.clipboard.writeText(whatsappMessage);
  }, [whatsappMessage]);

  const [linkCopied, setLinkCopied] = useState(false);
  const handleCopyLink = useCallback(async () => {
    if (!paymentUrl) return;
    try {
      await navigator.clipboard.writeText(paymentUrl);
    } catch {
      // Fallback for environments without clipboard API (e.g. headless browsers)
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  }, [paymentUrl]);

  /* ── Customer selection step ────────────────────────────────────────── */

  const handleSelectCustomer = useCallback((c: RivhitCustomer) => {
    setSelectedCustomer(c);
    setStep('invoices');
  }, []);

  const handleBackToCustomer = useCallback(() => {
    setSelectedCustomer(null);
    setInvoices([]);
    setAllocations([]);
    setPaymentUrl('');
    setError(null);
    setSmsSentTo(null);
    setStep('customer');
  }, []);

  const handleResetPaymentLink = useCallback(() => {
    setPaymentUrl('');
    setSmsSentTo(null);
    setError(null);
  }, []);

  /* ── Render ─────────────────────────────────────────────────────────── */

  const stepIndicator = (
    <div className="flex items-center gap-2 mb-5">
      {!customerKnown && (
        <>
          <button
            onClick={step === 'invoices' ? handleBackToCustomer : undefined}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              step === 'customer'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer'
            }`}
          >
            {step !== 'customer' && <span>✓</span>}
            <span>{t('paymentLinkWizard.stepCustomer')}</span>
          </button>
          <span className="text-gray-300">→</span>
        </>
      )}
      <span
        className={`rounded-full px-3 py-1 text-xs font-medium ${
          step === 'invoices' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
        }`}
      >
        {t('paymentLinkWizard.stepInvoices')}
      </span>
    </div>
  );

  const customerStepContent = (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">
        {t('paymentLinkWizard.selectCustomer')}
      </label>
      <input
        type="text"
        placeholder={t('paymentLinkWizard.searchCustomer')}
        value={customerSearch}
        onChange={(e) => setCustomerSearch(e.target.value)}
        className="mb-3 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        autoFocus
      />
      {isSearching && !customerOptions.length ? (
        <p className="py-8 text-center text-gray-500">{t('common.loading')}</p>
      ) : (
        <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-100">
          {searchLoading && customerOptions.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 text-xs text-gray-400 border-b border-gray-100">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500" />
              {t('common.searching')}
            </div>
          )}
          {customerOptions.map((c) => (
            <button
              key={c.customer_id}
              type="button"
              onClick={() => handleSelectCustomer(c)}
              className="flex w-full items-center justify-between px-4 py-3 text-left text-sm transition-colors hover:bg-gray-50 border-l-[3px] border-transparent"
            >
              <div>
                <span className="font-medium text-gray-900">{customerDisplayName(c)}</span>
                {c.phone && <span className="ml-3 text-gray-400">{c.phone}</span>}
              </div>
              <span className="text-xs text-gray-400">#{c.customer_id}</span>
            </button>
          ))}
          {!customerOptions.length && (
            <p className="py-8 text-center text-sm text-gray-400">{t('common.noResults')}</p>
          )}
        </div>
      )}
    </div>
  );

  const invoicesStepContent = (
    <div className="space-y-5">
      {/* Customer header */}
      {selectedCustomer && (
        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
          <div>
            <div className="text-sm font-medium text-gray-900">
              {customerDisplayName(selectedCustomer)}
            </div>
            <div className="text-xs text-gray-400">#{selectedCustomer.customer_id}</div>
          </div>
          {!customerKnown && (
            <button
              onClick={handleBackToCustomer}
              className="text-xs text-blue-600 hover:underline"
            >
              {t('paymentLinkWizard.changeCustomer')}
            </button>
          )}
        </div>
      )}

      {/* Invoice table */}
      <div>
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('paymentLinkWizard.selectInvoices')}
            </label>
          </div>
          {selectedTotal > 0 && (
            <div className="rounded-lg bg-green-50 px-3 py-2 text-right">
              <div className="text-[11px] font-medium uppercase tracking-wide text-green-700">
                {t('paymentLinkWizard.paymentAmount')}
              </div>
              <div className="text-sm font-semibold text-green-800">{formatCurrency(selectedTotal)}</div>
            </div>
          )}
        </div>

        {invoicesLoading ? (
          <p className="rounded-lg border border-gray-100 py-8 text-center text-sm text-gray-500">
            {t('common.loading')}
          </p>
        ) : invoices.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400">
            {t('paymentLinkWizard.noOpenInvoices')}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="w-10 px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allocations.length === invoices.length && invoices.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      title={t('paymentLinkWizard.selectAll')}
                    />
                  </th>
                  <th className="px-3 py-3 text-left">{t('paymentLinkWizard.invoice')}</th>
                  <th className="px-3 py-3 text-left">{t('paymentLinkWizard.date')}</th>
                  <th className="px-3 py-3 text-right">{t('paymentLinkWizard.openBalance')}</th>
                  <th className="px-3 py-3 text-right">{t('paymentLinkWizard.amountToClose')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoices.map((invoice) => {
                  const key = `${invoice.document_type}#${invoice.document_number}`;
                  const allocation = selectedSet.get(key);
                  const selected = !!allocation;
                  const amountTooHigh = selected && allocation.amount_close > invoice.open_balance + 0.01;
                  return (
                    <tr key={key} className={selected ? 'bg-green-50/50' : 'hover:bg-gray-50'}>
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleInvoice(invoice)}
                          className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-medium text-gray-900">#{invoice.document_number}</div>
                        {invoice.document_type_name && (
                          <div className="text-xs text-gray-400">{invoice.document_type_name}</div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-gray-600">{invoice.document_date || '-'}</td>
                      <td className="px-3 py-3 text-right font-medium text-gray-900">
                        {formatCurrency(invoice.open_balance)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <input
                          type="number"
                          min="0.01"
                          max={invoice.open_balance}
                          step="0.01"
                          disabled={!selected}
                          value={allocation?.amount_close ?? ''}
                          onChange={(e) => updateAllocationAmount(invoice, e.target.value)}
                          className={`w-32 rounded-lg border px-3 py-1.5 text-right text-sm disabled:bg-gray-50 disabled:text-gray-400 ${
                            amountTooHigh ? 'border-red-300 text-red-700' : 'border-gray-300'
                          }`}
                        />
                        {amountTooHigh && (
                          <div className="mt-1 text-xs text-red-600">
                            {t('paymentLinkWizard.amountExceedsBalance')}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {unknownBalanceCount > 0 && (
          <p
            className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            role="alert"
            data-testid="payment-link-balance-warning"
          >
            {t(
              'createDocument.invoices.unknownBalanceWarning',
              '{{count}} invoice balance could not be verified and cannot be selected until balances refresh.',
              { count: unknownBalanceCount },
            )}
          </p>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* SMS sent confirmation */}
      {smsSentTo && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          ✅ {t('paymentLinkWizard.smsSentConfirmation', 'Message successfully sent to')} {smsSentTo}
        </div>
      )}

      {/* Payment link placeholder — shown before the link is generated */}
      {!paymentUrl && selectedTotal > 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-gray-600">
              {t('paymentLinkWizard.linkPlaceholder', 'Payment link')}
            </div>
            <div className="rounded-md bg-white border border-gray-200 px-3 py-1 text-sm font-semibold text-gray-800">
              {formatCurrency(selectedTotal)}
            </div>
          </div>
          <div className="mt-2 h-7 rounded bg-gray-200 animate-pulse w-3/4 opacity-60" />
        </div>
      )}

      {/* Success & Message */}
      {paymentUrl && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="mb-4">
            <div className="text-sm font-medium text-green-800">{t('paymentLinkWizard.linkReady')}</div>
            <a
              href={paymentUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1 block break-all text-sm text-green-800 underline"
            >
              {paymentUrl}
            </a>
            <button
              type="button"
              onClick={handleCopyLink}
              className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-green-300 bg-white px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors"
            >
              {linkCopied ? '✓' : '📋'} {linkCopied
                ? t('paymentLinkWizard.linkCopied', 'Link copied!')
                : t('paymentLinkWizard.copyLink', 'Copy Link')}
            </button>
          </div>

          <div className="mt-4 border-t border-green-200 pt-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <label className="block text-xs font-semibold uppercase tracking-wider text-green-700">
                {t('paymentLinkWizard.generatedMessage', 'Generated Message')}
              </label>
              {whatsappTemplateValues && (
                <EditMessageTemplateModal
                  templateKey="payment_link_wizard_whatsapp"
                  values={whatsappTemplateValues}
                  onChanged={() => setTemplateRevision((current) => current + 1)}
                />
              )}
            </div>
            {(debtReportLoading || templateLoading) && (
              <div
                data-testid="payment-link-message-loading"
                className="mb-2 flex items-center gap-2 text-xs text-green-700"
              >
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600" />
                {t('paymentLinkWizard.debtReportLoading', 'Preparing current debt report link…')}
              </div>
            )}
            {(debtReportError || templateError) && (
              <div
                data-testid="payment-link-message-error"
                className="mb-2 flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
              >
                <span>{debtReportError || templateError}</span>
                <button
                  type="button"
                  onClick={handleRetryDebtReport}
                  className="font-medium underline whitespace-nowrap"
                >
                  {t('paymentLinkWizard.debtReportRetry', 'Retry')}
                </button>
              </div>
            )}
            <textarea
              rows={8}
              value={whatsappMessage}
              onChange={(event) => setWhatsappMessage(event.target.value)}
              className="w-full rounded-lg border border-green-200 bg-white p-3 text-sm text-gray-800 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Back to customer — left side */}
        {!customerKnown && (
          <button
            type="button"
            onClick={handleBackToCustomer}
            className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-1.5"
          >
            ← {t('paymentLinkWizard.backToCustomer', 'Back to Customer')}
          </button>
        )}

        {/* Right-side actions */}
        <div className="flex flex-wrap gap-3 ml-auto">
          {!inline && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
          )}
          {!paymentUrl ? (
            <button
              type="button"
              onClick={handleCreate}
              disabled={!canCreate}
              className="rounded-lg border border-green-600 px-5 py-2.5 text-sm font-semibold text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submitting
                ? t('paymentLinkWizard.creating')
                : t('paymentLinkWizard.createLink')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleResetPaymentLink}
                className="rounded-lg border border-gray-400 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                {t('paymentLinkWizard.generateNewLink', 'Generate New Payment Link')}
              </button>
              <button
                type="button"
                onClick={handleCopyMessage}
                disabled={!messageReady}
                className="rounded-lg border border-blue-600 px-5 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('paymentLinkWizard.copyMessage')}
              </button>
              <button
                type="button"
                onClick={handleSendWhatsApp}
                disabled={!messageReady || loggingWhatsApp}
                className="rounded-lg bg-green-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {loggingWhatsApp ? t('common.saving', 'Saving...') : t('paymentLinkWizard.sendWhatsAppAction', 'Send via WhatsApp')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setNotificationChannel('sms');
                  handleSendPaymentNotification();
                }}
                disabled={!canSendNotification}
                className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {sendingNotification
                  ? t('paymentLinkWizard.sendingNotification')
                  : t('paymentLinkWizard.sendSmsAction', 'Send via SMS')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const wizardContent = (
    <div className={inline ? '' : ''}>
      {stepIndicator}
      {step === 'customer' ? customerStepContent : invoicesStepContent}
    </div>
  );

  if (inline) {
    return (
      <div className="mx-auto max-w-3xl">
        <h1 className="mb-6 text-2xl font-bold text-gray-900">
          {t('paymentLinkWizard.title')}
        </h1>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          {wizardContent}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="relative mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('paymentLinkWizard.title')}
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
        {wizardContent}
      </div>
    </div>
  );
}
